import { TypedDocumentNode, gql } from '@apollo/client';
import styled, { css } from 'styled-components';

import { Title3 } from '@sorare/core/src/atoms/typography';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DailyEvent } from 'components/NeonChristmas/event';

import { CalendarDay_PromotionalEvent } from './__generated__/index.graphql';
import bottomUrl from './assets/bottom.png';
import ribbonUrl from './assets/ribbon.svg';
import sorareUrl from './assets/sorare.svg';
import topUrl from './assets/top.png';

const Illustration = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 50%;
`;
const Ribbon = styled.img.attrs({ src: ribbonUrl })`
  position: absolute;
  left: calc(5 * var(--unit));
  top: calc(7 * var(--unit));
  transform: translate(-50%, -50%) scale(0.75) rotate(-30deg);
  @media ${tabletAndAbove} {
    left: calc(8 * var(--unit));
    top: calc(8 * var(--unit));
    transform: translate(-50%, -50%) rotate(-30deg);
  }
`;
const Wrapper = styled.div<{
  isToday: boolean;
  isPast: boolean;
  isOpened: boolean;
}>`
  position: relative;
  border: 2px solid var(--c-nd-150-opaque);
  --illustration: url(${sorareUrl});
  background-color: var(--c-nd-50-opaque);
  background-image: url(${topUrl}), url(${bottomUrl});
  background-size:
    100% auto,
    100% auto;
  background-position:
    left top,
    left bottom;
  background-repeat: no-repeat;
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  box-sizing: border-box;
  overflow: clip;

  /* icon */
  svg {
    position: absolute;
    right: var(--double-unit);
    top: var(--double-unit);
  }
  ${({ isToday }) =>
    isToday &&
    css`
      border-color: var(--c-nd-1000);
      box-shadow:
        0px 0px 60px 0px rgba(var(--c-static-rgb-red-600), 0.8),
        inset 0px 0px 60px 100px rgba(var(--c-static-rgb-red-600), 0.25),
        0px 0px 4px 4px rgba(var(--c-static-rgb-red-600), 1),
        inset 0px 0px 4px 4px rgba(var(--c-static-rgb-red-600), 1);
    `}
  ${({ isPast }) =>
    isPast &&
    css`
      ${Illustration} {
        filter: grayscale(1);
      }
      opacity: 0.8;
    `}
  ${({ isPast, isOpened }) =>
    isPast &&
    isOpened &&
    css`
      ${Ribbon} {
        display: none;
      }
    `}
`;

type Props = {
  dailyEvent: DailyEvent;
  event?: CalendarDay_PromotionalEvent;
  style?: React.CSSProperties;
};

export const CalendarDay = ({ dailyEvent, event, style }: Props) => {
  // check must be made against current promotionalevent start date :/
  const currentDay = event?.startDate.getUTCDate();
  const { day, pictureUrl = sorareUrl } = dailyEvent;
  const isToday = currentDay === day;
  const isPast = currentDay !== undefined && currentDay > day;
  const { lifecycle } = useLifecycle();
  const openedDays = lifecycle?.[LIFECYCLE.neonChristmasCalendar] ?? [];
  const isOpened = openedDays.includes(day);

  return (
    <Wrapper
      style={style}
      isToday={isToday}
      isPast={isPast}
      isOpened={isOpened}
    >
      <Ribbon />
      <Title3>{day}</Title3>

      <Illustration src={pictureUrl} alt="" />
    </Wrapper>
  );
};

CalendarDay.fragments = {
  PromotionalEvent: gql`
    fragment CalendarDay_PromotionalEvent on PromotionalEventInterface {
      slug
      startDate
    }
  ` as TypedDocumentNode<CalendarDay_PromotionalEvent>,
};
